// Emergency upload of a release (08.10.2026). Pure decisions over plain
// values; the request itself is in ../thunk/upload.ts, the page in
// ../view/view.tsx. The hub checks everything again - this only spares the
// user a 190 MB upload that would be refused for a typo.

/** Role number of an administrator in the login token (DbEnum.AccessRole.Admin). */
export const ADMIN_ROLE = 2;

/** Largest package the hub accepts (ReleaseRegistration.MaxPackageBytes): 1 GiB. */
export const MAX_PACKAGE_BYTES = 1024 * 1024 * 1024;

/** Branches as the hub names them (DbEnum.SoftwareStatus), most stable first. */
export const BRANCHES = ["release", "beta", "alpha", "nightly", "buggy"] as const;
export type Branch = (typeof BRANCHES)[number];

export interface ReleaseNumber {
    readonly major: number;
    readonly minor: number;
}

export interface EmergencyForm {
    readonly software: string;
    readonly version: string;
    readonly branch: Branch | null;
    readonly sha256: string;
    readonly fileName: string | null;
    readonly fileSize: number | null;
}

export const EMPTY_FORM: EmergencyForm = {
    software: "Vodin",
    version: "",
    branch: null,
    sha256: "",
    fileName: null,
    fileSize: null,
};

/** Problems the form can have; each is a message id "EmergencyUpload:Problem:<id>". */
export type FormProblem =
    | "file-missing"
    | "file-empty"
    | "file-too-large"
    | "software-invalid"
    | "version-missing"
    | "version-invalid"
    | "branch-missing"
    | "sha256-invalid";

/**
 * Largest part of a release number the hub reads: UpdateChoice.ParseVersion
 * takes major and minor as int (Int32) without sign, so 0..2147483647.
 */
export const MAX_VERSION_PART = 2147483647;

/** One part of a release number: ASCII digits (leading zeros allowed) within 0..MAX_VERSION_PART, or null. */
function parseVersionPart(digits: string): number | null {
    const value = Number(digits);
    return value <= MAX_VERSION_PART ? value : null;
}

/**
 * "<major>.<minor>", digits only, blanks around allowed - like
 * UpdateChoice.ParseVersion on the hub, including its Int32 range
 * (anima_frontend#1): until 03.11.2026 99999999999.1 passed here and was
 * refused by the hub only after the package had travelled.
 */
export function parseVersion(text: string | null | undefined): ReleaseNumber | null {
    const match = /^([0-9]+)\.([0-9]+)$/.exec((text ?? "").trim());
    const major = match ? parseVersionPart(match[1]) : null;
    const minor = match ? parseVersionPart(match[2]) : null;
    return major !== null && minor !== null ? { major, minor } : null;
}

/** A branch name in any case, or null. */
export function parseBranch(text: string | null | undefined): Branch | null {
    const lower = (text ?? "").trim().toLowerCase();
    return (BRANCHES as readonly string[]).includes(lower) ? (lower as Branch) : null;
}

/** Blank = none (""), 64 hex digits = lower-cased, anything else null. */
export function normaliseSha256(text: string | null | undefined): string | null {
    const trimmed = (text ?? "").trim().toLowerCase();
    return trimmed === "" ? "" : /^[0-9a-f]{64}$/.test(trimmed) ? trimmed : null;
}

/** Letters, digits, "_" and "-", or empty (the hub then takes Vodin). */
export function isSoftwareName(text: string | null | undefined): boolean {
    return /^[A-Za-z0-9_-]{0,64}$/.test((text ?? "").trim());
}

/** Every problem of the form, in the order the page shows them. Empty = may be sent. */
export function judgeForm(form: EmergencyForm): readonly FormProblem[] {
    const checks: readonly [boolean, FormProblem][] = [
        [form.fileName === null || form.fileSize === null, "file-missing"],
        [form.fileSize === 0, "file-empty"],
        [(form.fileSize ?? 0) > MAX_PACKAGE_BYTES, "file-too-large"],
        [!isSoftwareName(form.software), "software-invalid"],
        [form.version.trim() === "", "version-missing"],
        [form.version.trim() !== "" && parseVersion(form.version) === null, "version-invalid"],
        [form.branch === null, "branch-missing"],
        [normaliseSha256(form.sha256) === null, "sha256-invalid"],
    ];
    return checks.filter(([failed]) => failed).map(([, problem]) => problem);
}

/**
 * The form fields in the order they are sent. The hub reads the fields
 * before the file and refuses a bad request before the package travels, so
 * the file must come last (it is appended by the request).
 */
export function formFields(form: EmergencyForm): readonly [string, string][] {
    return [
        ["software", form.software.trim()],
        ["version", form.version.trim()],
        ["branch", form.branch ?? ""],
        ["sha256", normaliseSha256(form.sha256) ?? form.sha256.trim()],
    ];
}

/** Whole percent of an upload, 0..100; unknown total counts as 0. */
export function progressPercent(loaded: number, total: number): number {
    return total > 0 ? Math.min(100, Math.max(0, Math.floor((loaded / total) * 100))) : 0;
}

/** Bytes as a short text: 190.4 MB. */
export function formatBytes(bytes: number): string {
    const units = ["B", "KB", "MB", "GB"];
    const exponent = bytes > 0 ? Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024))) : 0;
    const value = bytes / Math.pow(1024, exponent);
    return (exponent === 0 ? String(bytes) : value.toFixed(1)) + " " + units[exponent];
}

export interface Accepted {
    readonly kind: "accepted";
    readonly uuid: string;
    readonly version: string;
    readonly branch: string;
    readonly hash: string;
    readonly size: number;
    readonly precessor: string | null;
}

export interface Rejected {
    readonly kind: "rejected";
    readonly status: number;
    /** Stable code from the hub (e.g. "version-exists"), or a local one: "network", "unauthorized", "unexpected". */
    readonly code: string;
    readonly message: string;
}

export type UploadOutcome = Accepted | Rejected;

function parseJson(text: string): any {
    try {
        return JSON.parse(text);
    } catch {
        return null;
    }
}

/**
 * What an answer of POST api/Updates/emergency means. Total: every status
 * and body maps to an outcome. Status 0 is a broken connection.
 */
export function outcomeOf(status: number, bodyText: string): UploadOutcome {
    const body = parseJson(bodyText);
    if (status >= 200 && status < 300 && body && typeof body.hash === "string" && body.uuid) {
        return {
            kind: "accepted",
            uuid: String(body.uuid),
            version: String(body.version ?? ""),
            branch: String(body.branch ?? ""),
            hash: body.hash,
            size: Number(body.size ?? 0),
            precessor: body.precessor ? String(body.precessor) : null,
        };
    }

    const code = status === 0 ? "network"
        : status === 401 ? "unauthorized"
        : status === 413 && !(body && body.code) ? "file-too-large"
        : body && typeof body.code === "string" ? body.code
        : "unexpected";
    const message = body && typeof body.message === "string" ? body.message : "HTTP " + status;
    return { kind: "rejected", status, code, message };
}

/**
 * Whether the version is already registered for the software, in any branch
 * - the hub's rule (ReleaseRegistration.Conflict), compared by release
 * number. Asked before uploading, so a duplicate is refused before the
 * package travels. Empty software means Vodin, as on the hub.
 */
export function versionTaken(
    releases: readonly { software?: string | null; version?: string | null }[],
    software: string,
    version: string,
): boolean {
    const name = software.trim() === "" ? "Vodin" : software.trim();
    const wanted = parseVersion(version);
    return wanted !== null && releases.some((r) => {
        const number = parseVersion(r.version);
        return r.software === name && number !== null
            && number.major === wanted.major && number.minor === wanted.minor;
    });
}
